import { createPhotoQueue } from './reportPhotoQueue.mjs';

const DB_NAME = 'vatelsec-offline';
const DB_VERSION = 2;
const STORE = 'drafts';
const PHOTO_STORE = 'photos';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponible'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'agentId' });
      }
      if (!db.objectStoreNames.contains(PHOTO_STORE)) {
        db.createObjectStore(PHOTO_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function readLocalDraft(agentId) {
  if (!agentId) return null;
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    return await requestResult(tx.objectStore(STORE).get(agentId));
  } finally {
    db.close();
  }
}

export async function writeLocalDraft(record) {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function deleteLocalDraft(agentId) {
  if (!agentId) return;
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(agentId);
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

function finishTransaction(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

const reportPhotoQueue = createPhotoQueue({
  async put(record) {
    const db = await openDb();
    try {
      const tx = db.transaction(PHOTO_STORE, 'readwrite');
      tx.objectStore(PHOTO_STORE).put(record);
      await finishTransaction(tx);
    } finally {
      db.close();
    }
  },
  async all() {
    const db = await openDb();
    try {
      const tx = db.transaction(PHOTO_STORE, 'readonly');
      const rows = await requestResult(tx.objectStore(PHOTO_STORE).getAll());
      return rows || [];
    } finally {
      db.close();
    }
  },
  async delete(id) {
    const db = await openDb();
    try {
      const tx = db.transaction(PHOTO_STORE, 'readwrite');
      tx.objectStore(PHOTO_STORE).delete(id);
      await finishTransaction(tx);
    } finally {
      db.close();
    }
  },
  async clear() {
    const db = await openDb();
    try {
      const tx = db.transaction(PHOTO_STORE, 'readwrite');
      tx.objectStore(PHOTO_STORE).clear();
      await finishTransaction(tx);
    } finally {
      db.close();
    }
  },
});

export function queueReportPhoto(agentId, blob, reportId) {
  return reportPhotoQueue.add(agentId, blob, { reportId });
}

export function listReportPhotos(agentId) {
  return reportPhotoQueue.list(agentId);
}

export function tagReportPhoto(id, reportId) {
  return reportPhotoQueue.tag(id, reportId);
}

export function deleteReportPhoto(id) {
  return reportPhotoQueue.remove(id);
}

export function clearAgentPhotos(agentId) {
  return reportPhotoQueue.clearAgent(agentId);
}

export async function clearLocalDrafts() {
  const db = await openDb();
  try {
    const tx = db.transaction([STORE, PHOTO_STORE], 'readwrite');
    tx.objectStore(STORE).clear();
    tx.objectStore(PHOTO_STORE).clear();
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function listLocalDrafts() {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const all = await requestResult(tx.objectStore(STORE).getAll());
    return all || [];
  } finally {
    db.close();
  }
}
